import { gql, useLazyQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { RoutePageProps } from '../../App';
import { MAIN_MENU_ID } from '../../constants/layout';
import { ESPACE_PRIVE, LOGIN } from '../../constants/routing';
import { BASIC_IMAGE_GQL } from '../../graphql/ImageGql';
import { useProfile } from '../../hooks/useProfile';
import { StrapiEnTeteEntity, StrapiPiedPageEntity } from '../../models/layout';
import { Loader } from '../atoms/Loader';
import { ErrorOrSuccessBlock } from '../molecules/ErrorOrSuccessBlock';
import { Footer } from './Footer';
import { Header } from './Header';

type LayoutQuery = {
  enTeteConnecte?: StrapiEnTeteEntity;
  enTeteDeconnecte?: StrapiEnTeteEntity;
  piedPage: StrapiPiedPageEntity;
};

export const Layout = ({ locale }: RoutePageProps) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const isEspacePrive = ESPACE_PRIVE === location.pathname.split('/')[2];
  const { isLoggedIn, profile } = useProfile();
  const [isLoading, setIsLoading] = useState(true);

  const [loadAuthenticatedLayout, { error: authenticatedLayoutError, data: authenticatedLayoutData }] =
    useLazyQuery<LayoutQuery>(AUTHENTICATED_LAYOUT_QUERY);

  const [loadPublicLayout, { error: publicLayoutError, data: publicLayoutData }] =
    useLazyQuery<LayoutQuery>(PUBLIC_LAYOUT_QUERY);

  useEffect(() => {
    i18n.changeLanguage(locale).catch((err) => console.error('i18n.changeLanguage error:', err));
    document.documentElement.lang = locale;
  }, [locale, i18n]);

  useEffect(() => {
    if (authenticatedLayoutData) {
      setIsLoading(false);
    }
  }, [authenticatedLayoutData]);

  useEffect(() => {
    if (publicLayoutData) {
      setIsLoading(false);
    }
  }, [publicLayoutData]);

  useEffect(() => {
    if (isLoggedIn === undefined) {
      // profile loading
      return;
    }
    if (isEspacePrive && !isLoggedIn) {
      const urlEncoded = encodeURI(globalThis.location.pathname);
      const pathSegments = globalThis.location.pathname.split('/');
      const loc = pathSegments[1] || 'fr'; // fallback to 'fr' if no language segment
      globalThis.location.assign(LOGIN + '?RelayState=' + urlEncoded + '&language=' + loc);
      return;
    }
    if (isLoggedIn) {
      loadAuthenticatedLayout({ variables: { locale } }).catch(console.error);
    } else {
      loadPublicLayout({
        variables: { locale },
        context: { isPublic: true }
      }).catch(console.error);
    }
  }, [isLoggedIn, locale]);

  if (isLoading) return <Loader />;
  if (publicLayoutError || authenticatedLayoutError) return <ErrorOrSuccessBlock />;

  const enTete = isLoggedIn ? authenticatedLayoutData?.enTeteConnecte : publicLayoutData?.enTeteDeconnecte;
  const footer = isLoggedIn ? authenticatedLayoutData?.piedPage : publicLayoutData?.piedPage;

  return (
    <Box>
      <Header
        enTete={enTete}
        isLoggedIn={isLoggedIn}
        locale={locale}
        isAgendaAuthorized={profile?.isAgendaAuthorized}
      />
      {/* laisser le role main - demande rgaa */}
      <Box component={'main'} role="main" id={`${MAIN_MENU_ID}`} sx={{ zIndex: 2 }}>
        <Outlet />
      </Box>
      <Footer piedPage={footer} />
    </Box>
  );
};

const FOOTER_QUERY = `
  piedPage(locale: $locale) {
    Logo {
      ${BASIC_IMAGE_GQL}
    }
    LogoBCorp {
      ${BASIC_IMAGE_GQL}
    }
    LiensAGauche {
      Label
      Lien
    }
    LiensADroite {
      Label
      Lien
    }
    LiensReseauxSociaux {
      Label
      Lien
    }
    ScoreFruggr
  }
`;

const AUTHENTICATED_LAYOUT_QUERY = gql`
query GetLayoutConnecte($locale: I18NLocaleCode!) {
  enTeteConnecte(locale: $locale) {
    LienMenus {
      Label
      Lien
      SousMenus {
        Label
        Lien
      }
    }
  }
  ${FOOTER_QUERY}
}
`;

const PUBLIC_LAYOUT_QUERY = gql`
query GetLayoutDeconnecte($locale: I18NLocaleCode!) {
  enTeteDeconnecte(locale: $locale) {
    LienMenus {
      Label
      Lien
      SousMenus {
        Label
        Lien
      }
    }
  }
  ${FOOTER_QUERY}
}
`;
